/*
  Musterservice — „Als Muster bestellen" auf jeder Stoffseite.

  Aufbau: Im Warenkorb liegt höchstens EIN Posten „Stoffmuster" (0,00 €). Welche
  Stoffe der Kunde sehen will, hängt als Zeilen-Eigenschaft an genau diesem
  Posten: sichtbar „Stoff 1" … „Stoff 5", dazu die versteckte Liste `_stoffe`,
  aus der sich die Auswahl wieder einlesen lässt. So steht im Warenkorb eine
  Zeile statt fünf, die Bestellung nennt dem Zuschnitt trotzdem jeden Stoff mit
  Artikelnummer, und der Versand kostet einmal 2,00 € (eigenes Versandprofil).

  Der Warenkorb ist die einzige Quelle der Wahrheit — kein localStorage. Ein in
  einem anderen Tab entfernter Posten wäre sonst hier noch „ausgewählt", und der
  Kunde klickt gegen eine Anzeige, die nicht stimmt.
*/
(() => {
  const TRENNER = ';;';
  const FELD = '_stoffe';

  // Ein gemeinsamer Abruf für alle Knöpfe auf der Seite (verwandte Produkte!),
  // statt je Knopf einer.
  let cartLauf = null;
  function holeCart(frisch) {
    if (frisch || !cartLauf) {
      cartLauf = fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    return cartLauf;
  }

  const zeileFinden = (cart, variantId) =>
    ((cart && cart.items) || []).find((i) => Number(i.variant_id) === Number(variantId));

  function stoffeLesen(zeile) {
    const roh = zeile && zeile.properties ? zeile.properties[FELD] : '';
    if (!roh) return [];
    return String(roh)
      .split(TRENNER)
      .filter(Boolean)
      .map((teil) => {
        const [handle, label, sku] = teil.split('|');
        return { handle: handle || '', label: label || handle || '', sku: sku || '' };
      })
      .filter((s) => s.handle);
  }

  // Sichtbare Eigenschaften neu durchnummerieren, damit nach dem Entfernen eines
  // Stoffs keine Lücke („Stoff 1", „Stoff 3") in der Bestellung steht.
  //
  // Nur der Name, keine Artikelnummer: im Warenkorb stehen die Bilder der
  // gewählten Stoffe daneben, „(FAB-LIN-NAT-227)" sagt der Kundschaft nichts und
  // zerreißt die Zeile auf dem Telefon über drei Zeilen. Für den Zuschnitt steht
  // die Nummer weiterhin in `_stoffe`.
  function stoffeSchreiben(stoffe) {
    const props = {};
    props[FELD] = stoffe.map((s) => [s.handle, s.label, s.sku].join('|')).join(TRENNER);
    stoffe.forEach((s, i) => {
      props[`Stoff ${i + 1}`] = s.label;
    });
    return props;
  }

  // Wie meterware.js: wenn das Theme eine Schublade hat, deren Abschnitte
  // gleich mitrendern lassen – sonst bliebe der Warenkorb optisch stehen.
  function schubladeUndAbschnitte() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const kann = cart && typeof cart.renderContents === 'function' && typeof cart.getSectionsToRender === 'function';
    return { cart, kann };
  }

  async function schicke(pfad, body) {
    const { cart, kann } = schubladeUndAbschnitte();
    if (kann) {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(pfad, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${pfad}: ${res.status}`);
    const daten = await res.json();
    if (kann && daten && daten.sections) {
      if (cart.classList) cart.classList.remove('is-empty');
      cart.renderContents(daten);
    }
    return daten;
  }

  // Auf der Warenkorbseite steht kein Knopf, aus dem sich die Variante ablesen
  // ließe — layout/theme.liquid legt sie deshalb global ab.
  const konfig = () => window.stoffeyaMuster || null;

  // Ein Umschlag, und nur einer. Zwei Dinge gehen hier schief, wenn niemand
  // aufräumt:
  //
  // 1. Der Mengenzähler im Warenkorb ließ sich hochstellen (gemessen: 7 ×
  //    „Stoffmuster" zu 0,00 € bei einmal 2,00 € Porto — sieben Umschläge gratis).
  // 2. Es können ZWEI Musterzeilen entstehen: Wer zwei Stoffe schnell
  //    hintereinander anklickt, löst zwei Anfragen aus, die beide „noch keine
  //    Zeile da" lesen und je eine anlegen. Shopify legt sie nicht zusammen, weil
  //    ihre Eigenschaften verschieden sind. Danach pflegt die Oberfläche nur die
  //    erste, die zweite fährt als Geisterzeile mit.
  //
  // Beides wird beim nächsten Seitenaufruf still geradegezogen: die Stoffe aller
  // Musterzeilen wandern in die erste (Reihenfolge bleibt, Doppelte fliegen raus,
  // bei der Höchstzahl wird gekappt), die überzähligen Zeilen verschwinden, und
  // die Menge steht wieder auf 1.
  async function ordneMusterzeile(cart) {
    const cfg = konfig();
    if (!cfg || !cart) return cart;
    const zeilen = (cart.items || []).filter((i) => Number(i.variant_id) === Number(cfg.variantId));
    if (!zeilen.length) return cart;

    const erste = zeilen[0];
    const ueberzaehlig = zeilen.slice(1);
    const alle = [];
    zeilen.forEach((z) => stoffeLesen(z).forEach((stoff) => {
      if (!alle.some((x) => x.handle === stoff.handle)) alle.push(stoff);
    }));
    const behalten = alle.slice(0, Number(cfg.max) || 5);

    const istListe = stoffeLesen(erste).map((x) => x.handle).join('|');
    const sollListe = behalten.map((x) => x.handle).join('|');
    if (!ueberzaehlig.length && erste.quantity === 1 && istListe === sollListe) return cart;

    try {
      // Zeilenschlüssel sind nicht durchnummeriert, das Entfernen einer Zeile
      // macht die Schlüssel der anderen also nicht ungültig.
      for (const z of ueberzaehlig) await schicke('/cart/change.js', { id: z.key, quantity: 0 });
      if (erste.quantity !== 1 || istListe !== sollListe) {
        await schicke('/cart/change.js', behalten.length
          ? { id: erste.key, quantity: 1, properties: stoffeSchreiben(behalten) }
          : { id: erste.key, quantity: 0 });
      }
      return await holeCart(true);
    } catch (e) {
      return cart;
    }
  }

  // Die Mengenwahl blendet das Theme selbst aus (cart-item__quantity--muster),
  // hier bleibt nur der harte Riegel für Mengen, die anders entstanden sind.
  async function warenkorbWachen() {
    return ordneMusterzeile(await holeCart(true));
  }

  document.addEventListener('DOMContentLoaded', warenkorbWachen);
  if (document.readyState !== 'loading') warenkorbWachen();
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      // Nach dem Neuzeichnen des Warenkorbs ist das Mengenfeld ein neues Element.
      setTimeout(warenkorbWachen, 0);
    });
  }

  // Nach jedem Schreiben: Knöpfe auf den Stoffseiten nachziehen und dem Theme
  // sagen, dass sich der Warenkorb geändert hat.
  async function nachziehen() {
    const frisch = await holeCart(true);
    document.querySelectorAll('stoffeya-muster-button').forEach((el) => {
      if (typeof el.zeichne === 'function') el.zeichne(frisch);
    });
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'stoffeya-muster' });
    }
    return frisch;
  }

  // Einen einzelnen Stoff direkt im Warenkorb abwählen. Ohne das müsste, wer
  // sich bei einem von fünf vertut, den ganzen Umschlag löschen und die vier
  // richtigen noch einmal zusammensuchen.
  //
  // Der Griff geht über das Dokument, nicht über den Knopf: Die Schublade baut
  // ihre Zeilen nach jeder Änderung neu auf, ein direkt gebundener Zuhörer wäre
  // danach am alten, weggeworfenen Knopf.
  document.addEventListener('click', async (ereignis) => {
    const knopf = ereignis.target.closest('[data-muster-weg]');
    if (!knopf) return;
    ereignis.preventDefault();
    if (knopf.dataset.laeuft) return;
    knopf.dataset.laeuft = '1';
    try {
      const cfg = konfig();
      const zeile = zeileFinden(await holeCart(true), cfg && cfg.variantId);
      if (!zeile) return;
      const bleibt = stoffeLesen(zeile).filter((s) => s.handle !== knopf.dataset.musterWeg);
      await schicke('/cart/change.js', bleibt.length
        ? { id: zeile.key, quantity: 1, properties: stoffeSchreiben(bleibt) }
        : { id: zeile.key, quantity: 0 });   // der letzte Stoff: die Zeile hat keinen Zweck mehr
      await nachziehen();
    } catch (e) {
      /* Warenkorb bleibt, wie er war */
    } finally {
      delete knopf.dataset.laeuft;
    }
  });

  class MusterButton extends HTMLElement {
    connectedCallback() {
      this.knopf = this.querySelector('.muster__button');
      this.text = this.querySelector('.muster__text');
      this.hinweis = this.querySelector('.muster__hint');
      if (!this.knopf) return;
      this.variantId = Number(this.dataset.variant);
      this.max = Number(this.dataset.max) || 5;
      this.handle = this.dataset.handle;
      this.beschriftungen = {
        add: this.text.textContent.trim(),
        added: this.dataset.labelAdded || 'Muster ausgewählt',
        full: this.dataset.labelFull || 'Musterauswahl voll',
        hint: this.hinweis ? this.hinweis.textContent.trim() : '',
      };
      this.knopf.addEventListener('click', () => this.klick());
      holeCart().then((cart) => this.zeichne(cart));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.abmelden = subscribe(PUB_SUB_EVENTS.cartUpdate, () =>
          holeCart(true).then((cart) => this.zeichne(cart))
        );
      }
    }

    disconnectedCallback() {
      if (typeof this.abmelden === 'function') this.abmelden();
    }

    zeichne(cart) {
      const stoffe = stoffeLesen(zeileFinden(cart, this.variantId));
      const drin = stoffe.some((s) => s.handle === this.handle);
      const voll = !drin && stoffe.length >= this.max;
      this.classList.toggle('muster--gewaehlt', drin);
      this.knopf.setAttribute('aria-pressed', drin ? 'true' : 'false');
      this.knopf.disabled = voll;
      this.text.textContent = drin
        ? this.beschriftungen.added
        : voll
          ? this.beschriftungen.full
          : this.beschriftungen.add;
      if (this.hinweis) {
        this.hinweis.textContent = stoffe.length
          ? `${stoffe.length} von ${this.max} — ${this.beschriftungen.hint}`
          : this.beschriftungen.hint;
      }
    }

    async klick() {
      if (this.laeuft) return;
      this.laeuft = true;
      this.knopf.classList.add('muster__button--laeuft');
      try {
        const cart = await holeCart(true);
        const zeile = zeileFinden(cart, this.variantId);
        let stoffe = stoffeLesen(zeile);
        const drin = stoffe.some((s) => s.handle === this.handle);

        if (drin) {
          stoffe = stoffe.filter((s) => s.handle !== this.handle);
        } else {
          if (stoffe.length >= this.max) return;
          stoffe.push({ handle: this.handle, label: this.dataset.label || this.handle, sku: this.dataset.sku || '' });
        }

        if (!zeile) {
          await schicke('/cart/add.js', {
            items: [{ id: this.variantId, quantity: 1, properties: stoffeSchreiben(stoffe) }],
          });
        } else if (stoffe.length) {
          // Eigenschaften lassen sich nur beim Ändern der ganzen Zeile setzen;
          // Shopify vergibt dabei einen neuen `key`, deshalb danach neu lesen.
          await schicke('/cart/change.js', { id: zeile.key, quantity: 1, properties: stoffeSchreiben(stoffe) });
        } else {
          // Letzter Stoff abgewählt: die Zeile hat keinen Zweck mehr.
          await schicke('/cart/change.js', { id: zeile.key, quantity: 0 });
        }

        await nachziehen();
      } catch (e) {
        if (this.hinweis) this.hinweis.textContent = this.dataset.labelError || 'Das hat gerade nicht geklappt.';
      } finally {
        this.laeuft = false;
        this.knopf.classList.remove('muster__button--laeuft');
      }
    }
  }

  if (!customElements.get('stoffeya-muster-button')) {
    customElements.define('stoffeya-muster-button', MusterButton);
  }
})();
